import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SectionHeader from "../../components/SectionHeader";
import { TrackIcon } from "../../components/icons";
import {
  formatCount,
  getContentTypeSlug,
  getStatusOptions,
  getStatusSlug,
  orderContentTypesByCount,
} from "../../utils/utils";

// Every trackable status (all but "none"), as TrackDispatcher derives them.
// Counts come from get_user_counts, so a new status also needs counting there.
const COUNT_STATUSES = Object.keys(getStatusOptions()).filter(
  (status) => status !== "none",
);
// The All view leads with finished, the section's headline status
const ALL_VIEW_STATUSES = [
  "finished",
  ...COUNT_STATUSES.filter((status) => status !== "finished"),
];

// Title nouns per type: [singular, plural]
const TRACKED_NOUNS = {
  movie: ["movie", "movies"],
  tvshow: ["TV series", "TV series"],
  book: ["book", "books"],
};
// Card labels in the All view (shown from sm up, beside icon + count)
const CARD_LABELS = { movie: "movies", tvshow: "TV", book: "books" };
// From lg there's room to say what TV counts are
const WIDE_CARD_LABELS = { ...CARD_LABELS, tvshow: "TV series" };

// "1 episode" / "1,240 episodes"; null for 0 so the part is left out
const amount = (n, unit) =>
  n > 0 ? `${n.toLocaleString()} ${unit}${n === 1 ? "" : "s"}` : null;

/**
 * One count tile: icon + bold count (+ label where there's room). A link to
 * Track when given onClick; a plain label otherwise. Without an icon it's just
 * the count (the All view's table, where the row says which status).
 */
function CountCard({ icon, count, label, onClick, filled = !!onClick }) {
  const content = (
    <CardContent
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        py: 1,
        px: 0,
        // MUI pads a CardContent that's its parent's last child by 24px —
        // which it is without the action area — doubling the tile's height
        "&:last-child": { pb: 1 },
      }}
    >
      {icon}
      <Typography variant="body2">
        <b>{formatCount(count)}</b>
        {label && ` ${label}`}
      </Typography>
    </CardContent>
  );

  return (
    <Card
      elevation={0}
      // Filled by default only when tappable, so plain tiles (the table's
      // column headers) read as labels rather than buttons
      sx={{
        backgroundColor: (theme) =>
          filled ? theme.palette.secondary.main : "transparent",
      }}
    >
      {onClick ? (
        <CardActionArea onClick={onClick}>{content}</CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
}

/**
 * The profile's closing stats section: a headline count of everything
 * tracked, a line of finished totals (count, then movie hours / TV episodes /
 * book pages), and count cards linking to Track.
 *
 * All: totals across every type, then a column per type (most tracked first)
 * headed by a non-clickable total. From lg, each type's status cards (icon +
 * count, finished first) sit in a row under it; below lg there isn't the
 * width, so it becomes a table — a row per status labelled by its icon, with
 * just the counts in the cells.
 * A single type: "{n} movies tracked", its own finished totals, and a row of
 * status cards.
 *
 * Counts and totals come from the profile payload (get_user_counts). TV counts
 * shows (series); its episodes are those of finished seasons.
 */
export default function ProfileCounts({ profile, contentType }) {
  const { libraryCounts = {}, username } = profile;
  const theme = useTheme();
  const statusOptions = getStatusOptions(theme);
  const navigate = useNavigate();
  const isBelowSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isAll = contentType === "all";
  const typeCounts = libraryCounts[contentType] ?? {};

  const goToTrack = (type, status) =>
    navigate(
      `/track/u/${username}/${getContentTypeSlug(type)}/${getStatusSlug(status)}`,
    );

  // The type order everything in the All view follows: this person's most
  // tracked first (matching the top 5 stack above)
  const allTypes = orderContentTypesByCount(
    Object.fromEntries(
      Object.entries(libraryCounts).map(([type, c]) => [type, c.total]),
    ),
  );

  // Headline: everything tracked, any status. Subtitle: what's finished, plus
  // the most granular totals we can state accurately for it — movie hours, TV
  // episodes, book pages. All: "120 finished: 142 film hours · 840 TV episodes
  // · 12,340 pages"; a single type: "80 finished · 142 hours watched". Full
  // numbers here, unlike the tiles: they're personal milestones.
  const hours = (counts) => Math.round((counts?.finishedMinutes ?? 0) / 60);
  let title;
  let finished;
  let subtitle;
  if (isAll) {
    const counts = Object.values(libraryCounts);
    const total = counts.reduce((sum, c) => sum + (c.total ?? 0), 0);
    title = `${total.toLocaleString()} ${total === 1 ? "thing" : "things"} tracked`;
    finished = counts.reduce((sum, c) => sum + (c.finished ?? 0), 0);
    // In column order, so the line reads in the same order as the table
    const typeDetail = {
      movie: amount(hours(libraryCounts.movie), "film hour"),
      tvshow: amount(libraryCounts.tvshow?.finishedEpisodes ?? 0, "TV episode"),
      book: amount(libraryCounts.book?.finishedPages ?? 0, "page"),
    };
    const details = allTypes
      .map(({ value }) => typeDetail[value])
      .filter(Boolean);
    subtitle =
      `${finished.toLocaleString()} finished` +
      (details.length ? `: ${details.join(" · ")}` : "");
  } else {
    const total = typeCounts.total ?? 0;
    const [singular, plural] = TRACKED_NOUNS[contentType] ?? ["item", "items"];
    title = `${total.toLocaleString()} ${total === 1 ? singular : plural} tracked`;
    finished = typeCounts.finished ?? 0;
    const detail = {
      movie: amount(hours(typeCounts), "hour"),
      tvshow: amount(typeCounts.finishedEpisodes ?? 0, "episode"),
      book: amount(typeCounts.finishedPages ?? 0, "page"),
    }[contentType];
    subtitle =
      `${finished.toLocaleString()} finished` +
      (detail
        ? ` · ${detail} ${contentType === "book" ? "read" : "watched"}`
        : "");
  }
  // Nothing finished: no subtitle
  if (finished === 0) subtitle = null;

  // A type's four status cards. Labels only when a single type's row has the
  // full width; in the All view's narrower columns it's icon + number.
  const statusCards = (type, showLabels, statuses = COUNT_STATUSES) =>
    statuses.map((status) => (
      <CountCard
        key={status}
        icon={statusOptions[status]?.icon}
        count={libraryCounts[type]?.[status]}
        label={showLabels ? statusOptions[status]?.label.toLowerCase() : null}
        onClick={() => goToTrack(type, status)}
      />
    ));

  return (
    <Box mb={2}>
      <SectionHeader title={title} icon={<TrackIcon color="primary" />} />
      {subtitle && (
        <Typography variant="body2" color="text.secondary" mb={0.75}>
          {subtitle}
        </Typography>
      )}
      {isAll && isLgUp ? (
        // Wide enough for each type's statuses to sit in a row under its total
        <Grid container spacing={1}>
          {allTypes.map(({ value, icon }) => (
            <Grid item xs={4} key={value}>
              <CountCard
                icon={icon}
                count={libraryCounts[value]?.total}
                label={WIDE_CARD_LABELS[value]}
                filled
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 1,
                  mt: 1,
                }}
              >
                {statusCards(value, false, ALL_VIEW_STATUSES)}
              </Box>
            </Grid>
          ))}
        </Grid>
      ) : isAll ? (
        // Narrower: a table — a column of status icons, then a column per
        // type headed by its total, with just the counts in the cells
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `auto repeat(${allTypes.length}, 1fr)`,
            gap: 1,
          }}
        >
          <Box />
          {allTypes.map(({ value, icon }) => (
            <CountCard
              key={value}
              icon={icon}
              count={libraryCounts[value]?.total}
              label={isBelowSm ? null : CARD_LABELS[value]}
            />
          ))}
          {ALL_VIEW_STATUSES.map((status) => (
            <React.Fragment key={status}>
              <Box
                title={statusOptions[status]?.label}
                sx={{ display: "flex", alignItems: "center", pr: 0.5 }}
              >
                {statusOptions[status]?.icon}
              </Box>
              {allTypes.map(({ value }) => (
                <CountCard
                  key={value}
                  count={libraryCounts[value]?.[status]}
                  onClick={() => goToTrack(value, status)}
                />
              ))}
            </React.Fragment>
          ))}
        </Box>
      ) : (
        <Grid container spacing={1}>
          {statusCards(contentType, !isBelowSm).map((card) => (
            <Grid item xs={3} key={card.key}>
              {card}
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
